import { FileMimeType } from '@/models/FileMimeType';
import FileSaver from 'file-saver';
import { EffectiveDate } from '@/models/form/PersonalDetails.model';
import i18n from '@/lang/lang';

export class Utils {

  public static getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  public static downloadBase64File(base64Str: string, fileName: string, fileMimeType?: FileMimeType, extension?: string)
    : void {
    const mimeTypeArr = fileName.split('.');
    if (fileMimeType) {
      switch (fileMimeType) {
        case FileMimeType.PDF: {
          fileName = Utils.addSuffixToFile(fileName, extension || 'pdf');
          break;
        }
        case FileMimeType.CSV: {
          fileName = Utils.addSuffixToFile(fileName, extension || 'csv');
          break;
        }
        case FileMimeType.EXCEL: {
          fileName = Utils.addSuffixToFile(fileName, extension || 'xls');
          break;
        }
        case FileMimeType.TIFF: {
          fileName = Utils.addSuffixToFile(fileName, extension || 'tif');
          break;
        }
        case FileMimeType.TXT: {
          fileName = Utils.addSuffixToFile(fileName, extension || 'txt');
          break;
        }
      }
    } else {
      fileName = Utils.addSuffixToFile(fileName, mimeTypeArr[mimeTypeArr.length - 1]);
    }
    const raw = window.atob(base64Str);
    const array = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; i++) {
      array[i] = raw.charCodeAt(i);
    }
    const blob = new Blob([array], { type: fileMimeType || mimeTypeArr[mimeTypeArr.length - 1] });
    this.downloadBlobFile(blob, fileName);
  }

  public static downloadBlobFile(blob: Blob, fileName: string): void {
    FileSaver.saveAs(blob, fileName);
  }

  public static isMobile(): boolean {
    return window.innerWidth <= 1140;
  }

  public static isCompatibleBrowser(): boolean {
    const ua: any = window.navigator.userAgent;
    const ie11: any = ua.indexOf('Trident/');
    const edge: any = ua.indexOf('Edge/');
    const isChrome: any = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    // tslint:disable-next-line:triple-equals
    const isSafari: any = ua.indexOf('Safari') != -1;
    // tslint:disable-next-line:triple-equals
    const isFirefox: any = ua.indexOf('Firefox') != -1;
    const isNativeAndroid: any = ((ua.indexOf('Mozilla/5.0') > -1 && ua.indexOf('Android ') > -1 &&
      ua.indexOf('AppleWebKit') > -1) && (ua.indexOf('Version') > -1));
    return ie11 > 0 || edge > 0 || isChrome || isSafari || isFirefox || isNativeAndroid;
  }

  public static isLaptop(): boolean {
    return window.innerWidth <= 1366;
  }

  public static isTablet(): boolean {
    return window.innerWidth <= 960;
  }

  public static stringToDate(dateStr: string): number {
    if (dateStr !== '') {
      const year: any = dateStr.substring(0, 4);
      const month: any = dateStr.substring(4, 6);
      const day: any = dateStr.substring(6, 8);
      return new Date().setFullYear(year, +month - 1, day);
    } else {
      return -1;
    }
  }
  public static YYYYMMDDToDDMMYYYY(dateStr: string): string {
    if (dateStr !== '') {
      const year: any = dateStr.substring(0, 4);
      const month: any = dateStr.substring(4, 6);
      const day: any = dateStr.substring(6, 8);
      return day + '/' + month + '/' + year;
    } else {
      return '';
    }
  }

  public static DDMMYYYYStringToDate(dateStr: string): number {
    if (dateStr !== '') {
      const day: any = dateStr.substring(0, 2);
      const month: any = dateStr.substring(3, 5);
      const year: any = dateStr.substring(6, 10);
      return new Date().setFullYear(year, +month - 1, day);
    } else {
      return -1;
    }
  }

  public static dateToString(date: Date | string) {
        if (date) {
      date = new Date(date);
      const mm: any = date.getMonth() + 1; // getMonth() is zero-based
      const dd: any = date.getDate();
      return [date.getFullYear(),
        (mm > 9 ? '' : '0') + mm,
        (dd > 9 ? '' : '0') + dd,
      ].join('');
    } else {
      return '';
    }
  }

  public static isIEorEDGE(): boolean {
    return navigator.appName === 'Microsoft Internet Explorer' || (navigator.appName === 'Netscape'
      && navigator.appVersion.indexOf('Edge') > -1);
  }

  public static FullDateToString(date: Date): string {
    if (date) {
      date = new Date(date);
      const mm: any = date.getMonth() + 1; // getMonth() is zero-based
      const dd: any = date.getDate();
      const hh: any = date.getHours();
      const minutes: any = date.getMinutes();
      return [
        (dd > 9 ? '' : '0') + dd,
        (mm > 9 ? '/' : '/0') + mm,
        '/' + date.getFullYear(),
        (hh > 9 ? ' ' : ' 0') + hh,
        (minutes > 9 ? ':' : ':0') + minutes,
      ].join('');
    } else {
      return '';
    }
  }

  public static DDMMYYYYDateToString(date: Date | string): string {
    if (date) {
      date = new Date(date);
      const mm: any = date.getMonth() + 1; // getMonth() is zero-based
      const dd: any = date.getDate();
      return [
        (dd > 9 ? '' : '0') + dd,
        (mm > 9 ? '/' : '/0') + mm,
        '/' + date.getFullYear(),
      ].join('');
    } else {
      return '';
    }
  }

  public static checkIpsNotEmpty(ipRanges) {
    const newIpRanges: any[] = [];
    const ipToNum = (ip) => {
      return Number(
        ip.split('.')
          .map(d => ('000' + d).substr(-3))
          .join(''),
      );
    };
    ipRanges.forEach(rang => {
      const startIp = !!ipToNum(rang[0]);
      const endIp = !!ipToNum(rang[1]);
      if (startIp && endIp) {
        newIpRanges.push(rang);
      }
    });
    return newIpRanges;
  }

  private static addSuffixToFile(fileName: string, extension: string) {
    const suffix: string = '.' + extension;
    if (!fileName.endsWith(suffix) && !fileName.endsWith(suffix.toUpperCase())) {
      fileName += suffix;
    }
    return fileName;
  }

  private static getMobileOperatingSystem(): string {
    // var userAgent = window["opera"] || navigator.vendor || navigator.userAgent;
    const userAgent = navigator.vendor || navigator.userAgent;

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
      return 'Windows Phone';
    }

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    // if (/iPad|iPhone|iPod/.test(userAgent) && !window['MSStream']) {
    //   return 'iOS'
    // }
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }


    return 'unknown';
  }

}

export function isEmptyObject(obj: any): boolean {
  return Object.keys(obj).length > 0;
}

export function downloadLocalFile(file: any) {
  // @ts-ignore
  const URL: any = window.URL || window.webkitURL;
  if (URL && URL.createObjectURL) {
    file.blob = URL.createObjectURL(file.file);
  }
  const link: any = document.createElement('a');
  link.href = file.blob;
  link.download = file.name;
  link.click();
}

export function humanFileSize(size: any) {
  const i: any | number | bigint = Math.floor(Math.log(size) / Math.log(1024));
  // @ts-ignore
  return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}

export function checkSelectObject(obj: any, field: any): any {
  if (obj && obj[field]) {
    return obj[field];
  }
  return null;
}
